import React from 'react';
import { useTranslation } from '@/i18n';
import { Selector } from '@/components/ui/inputs/selector';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { TransferPanel } from '@/components/ui/panels';
import { useUserProfiling } from '@/hooks/authorization/useUserProfiling';


export const UserProfilingForm: React.FC = () => {
  const { t: translate } = useTranslation();
  const {
    roleOptions,
    selectedRoleId,
    onRoleChange,
    nonMembers,
    filteredNonMembers,
    isNonMembersLoading,
    hasMoreNonMembers,
    nonMembersSearchText,
    onNonMembersSearch,
    nonMembersSentinelRef,
    nonMembersScrollContainerRef,
    totalNonMembers,
    members,
    filteredMembers,
    isMembersLoading,
    hasMoreMembers,
    membersSearchText,
    onMembersSearch,
    membersSentinelRef,
    membersScrollContainerRef,
    totalMembers,
    selectedNonMemberIds,
    onToggleNonMember,
    onSelectAllNonMembers,
    isAllNonMembersSelected,
    isNonMembersIndeterminate,
    selectedMemberIds,
    onToggleMember,
    onSelectAllMembers,
    isAllMembersSelected,
    isMembersIndeterminate,
    onAdd,
    onRemove,
    onDoubleClickNonMember,
    onDoubleClickMember,
    onAddAllFromServer,
    onRemoveAllFromServer,
    isAddAllPending,
    isRemoveAllPending,
    isDirty,
    isSaving,
    onSave,
    onCancel,
  } = useUserProfiling();

  return (
    <div className="flex flex-col gap-6" data-testid="user-profiling-page">
      {/* Role selector */}
      <div className="w-96">
        <Selector
          label={translate('authorization:userProfiling.role')}
          options={roleOptions}
          value={selectedRoleId ? String(selectedRoleId) : undefined}
          onChange={(value) => onRoleChange(value ? Number(value) : null)}
          data-testid="user-profiling-role-selector"
        />
      </div>

      <TransferPanel
        keyExtractor={(member) => member.employeeId}
        renderTitle={(member) => member.employeeName}
        renderSubtitle={(member) => member.employeeTaxIdentificationNumber}
        leftPanel={{
          title: translate('authorization:userProfiling.unassigned'),
          items: filteredNonMembers,
          allItems: nonMembers,
          isLoading: isNonMembersLoading,
          totalRecords: totalNonMembers,
          searchValue: nonMembersSearchText,
          onSearch: onNonMembersSearch,
          searchPlaceholder: translate('authorization:userProfiling.search'),
          selectedIds: selectedNonMemberIds,
          onToggleSelect: onToggleNonMember,
          onSelectAll: onSelectAllNonMembers,
          isAllSelected: isAllNonMembersSelected,
          isIndeterminate: isNonMembersIndeterminate,
          onDoubleClick: onDoubleClickNonMember,
          // Ocultar banner de "Agregar todos" cuando ya hay una operación bulk pendiente
          onBulkAction: isRemoveAllPending ? undefined : onAddAllFromServer,
          bulkActionMessage: translate('authorization:userProfiling.addAllBannerMessage', { total: totalNonMembers }),
          sentinelRef: nonMembersSentinelRef,
          scrollContainerRef: nonMembersScrollContainerRef,
          selectAllHint: hasMoreNonMembers ? translate('authorization:userProfiling.selectAllHint', { loaded: nonMembers.length }) : undefined,
          footerText: (isRemoveAllPending && hasMoreNonMembers)
            ? translate('authorization:userProfiling.footerSavePending', { pending: totalNonMembers - nonMembers.length })
            : hasMoreNonMembers
              ? translate('authorization:userProfiling.footerPartial', { selected: selectedNonMemberIds.size, loaded: nonMembers.length, total: totalNonMembers })
              : translate('authorization:userProfiling.footer', { selected: selectedNonMemberIds.size, total: nonMembers.length }),
          'data-testid': 'non-members-panel',
        }}
        rightPanel={{
          title: translate('authorization:userProfiling.assigned'),
          items: filteredMembers,
          allItems: members,
          isLoading: isMembersLoading,
          totalRecords: totalMembers,
          searchValue: membersSearchText,
          onSearch: onMembersSearch,
          searchPlaceholder: translate('authorization:userProfiling.search'),
          selectedIds: selectedMemberIds,
          onToggleSelect: onToggleMember,
          onSelectAll: onSelectAllMembers,
          isAllSelected: isAllMembersSelected,
          isIndeterminate: isMembersIndeterminate,
          onDoubleClick: onDoubleClickMember,
          // Ocultar banner de "Quitar todos" cuando ya hay una operación bulk pendiente
          onBulkAction: isAddAllPending ? undefined : onRemoveAllFromServer,
          bulkActionMessage: translate('authorization:userProfiling.removeAllBannerMessage', { total: totalMembers }),
          sentinelRef: membersSentinelRef,
          scrollContainerRef: membersScrollContainerRef,
          selectAllHint: hasMoreMembers ? translate('authorization:userProfiling.selectAllHint', { loaded: members.length }) : undefined,
          footerText: (isAddAllPending && hasMoreMembers)
            ? translate('authorization:userProfiling.footerSavePending', { pending: totalMembers - members.length })
            : hasMoreMembers
              ? translate('authorization:userProfiling.footerPartial', { selected: selectedMemberIds.size, loaded: members.length, total: totalMembers })
              : translate('authorization:userProfiling.footer', { selected: selectedMemberIds.size, total: members.length }),
          'data-testid': 'members-panel',
        }}
        onAdd={onAdd}
        onRemove={onRemove}
        isAddDisabled={selectedNonMemberIds.size === 0}
        isRemoveDisabled={selectedMemberIds.size === 0}
        data-testid="user-profiling-transfer"
      />

      {/* Footer actions */}
      <div className="flex justify-center gap-3 pt-2">
        <PrimaryButton
          variant="outline"
          onClick={onCancel}
          disabled={!isDirty || isSaving}
          data-testid="cancel-button"
        >
          {translate('authorization:userProfiling.cancel')}
        </PrimaryButton>
        <PrimaryButton
          onClick={() => { void onSave(); }}
          isLoading={isSaving}
          disabled={!isDirty || isSaving}
          data-testid="save-button"
        >
          {translate('authorization:userProfiling.saveChanges')}
        </PrimaryButton>
      </div>
    </div>
  );
};

export default UserProfilingForm;

